import {
    useQuery,
    useInfiniteQuery,
    InfiniteData,
} from '@tanstack/react-query';
import {
    businessApiRoute,
    get,
    getBusinessId,
    setBusinessId,
    userApiRoute,
} from './Api';
import {
    Form,
    type Event,
    type ClientModel,
    FormSubmissionDetails,
    ReportData,
    ReportResponse,
} from './Api.types';
import { ReportType } from '~/pages/business/ReportsIndexPage/Report.types';
import { useEffect, useMemo } from 'react';

function smartRetry(failureCount, error) {
    if (error?.response?.status === 404 || error?.response?.status === 403) {
        return false;
    }
    return failureCount < 3;
}

let queryClient = null;
export function setQueryClient(qc) {
    queryClient = qc;
}

export function clearQueries() {
    queryClient.clear();
}

export function useUserInfoQuery() {
    return useQuery({
        queryKey: ['userinfo'],
        queryFn: () => get(userApiRoute('/userinfo')),
    });
}

let previousOnNotFound: (() => void) | null = null;
export function useBusinessInfo(onNotFound?: () => void) {
    if (onNotFound) {
        previousOnNotFound = onNotFound;
    }

    return useQuery({
        queryKey: ['businessinfo', getBusinessId()],
        queryFn: async () => {
            try {
                return await get(businessApiRoute('/info'));
            } catch (e) {
                const code = e?.response?.status;
                console.log('code=', code, e);

                if (e?.error === 'interaction_required') {
                    // Auth0 error!
                    document.location.href = '/logout';
                }

                const notFoundFn = onNotFound || previousOnNotFound;
                if (code === 404 && notFoundFn) {
                    notFoundFn();
                }

                throw e;
            }
        },
    });
}

export function setBusinessInfo(data) {
    queryClient.setQueryData(['businessinfo', getBusinessId()], old => data);
}

export function useFormsQuery() {
    return useQuery<Array<Form>>({
        queryKey: [getBusinessId(), 'forms'],
        queryFn: () => get(businessApiRoute(`/forms`)),
    });
}

export function useReportQuery<
    ExpectedReportObject = { [key: string]: unknown },
>(type: ReportType) {
    let reportUrl;
    switch (type) {
        case ReportType.Classes:
            reportUrl = 'classes';
            break;
        case ReportType.Clients:
            reportUrl = 'clients';
            break;
        case ReportType.Forms:
            reportUrl = 'forms';
            break;
        case ReportType.Events:
            reportUrl = 'events';
            break;
        case ReportType.Payments:
            reportUrl = 'payments';
            break;
        case ReportType.PackageGrants:
            reportUrl = 'package-grants';
            break;
        case ReportType.GiftCertificates:
            reportUrl = 'gift-certificates';
            break;
        default:
            throw new Error(`Invalid report type: ${type}`);
    }

    // TODO Make all of those endpoints return cursror-based pagination data

    const {
        data,
        isLoading,
        fetchNextPage,
        isFetchingNextPage,
        isSuccess,
        hasNextPage,
    } = useInfiniteQuery<
        ReportResponse<ExpectedReportObject>,
        unknown,
        InfiniteData<ExpectedReportObject> &
            ReportResponse<ExpectedReportObject>
    >({
        queryKey: [getBusinessId(), 'reports', reportUrl],
        queryFn: ({ pageParam }) =>
            get(
                businessApiRoute(`/reports/${reportUrl}`),
                pageParam ? { cursor: `${pageParam}` } : undefined,
            ),
        retry: 12,
        getPreviousPageParam: lastPage => lastPage.previous_cursor,
        getNextPageParam: lastPage => lastPage.next_cursor,
        initialPageParam: undefined,
        // @ts-expect-error incorrect tanstack types
        select: ({ pages }) => {
            // Flatten the pages into a single array of items
            const allItems = pages.flatMap(page => page.data);
            // Find the first non-empty keys value
            const keys = pages.find(page => page.keys)?.keys || [];

            return {
                data: allItems,
                keys,
            };
        },
    });

    // const  = useReportQuery<EventsReportObject>(ReportType.Events);

    useEffect(() => {
        if (!isLoading && isSuccess && hasNextPage && !isFetchingNextPage) {
            fetchNextPage(); // Fetch the next page
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasNextPage, isFetchingNextPage, isLoading, isSuccess]);

    return useMemo(
        () => ({
            data,
            isFetchingNextPage,
            isLoading,
        }),
        [data, isFetchingNextPage, isLoading],
    );
}

export function useFormSubmissionsQuery(opts) {
    const params = new URLSearchParams(opts).toString();
    return useQuery({
        queryKey: [getBusinessId(), 'form-submissions', JSON.stringify(opts)],
        queryFn: () => get(businessApiRoute(`/submissions?${params}`)),
    });
}

export function setFormsQuery(data) {
    queryClient.setQueryData([getBusinessId(), 'forms'], old => data);
}

export function useBookingFormQuery() {
    return useQuery({
        queryKey: [getBusinessId(), 'booking-forms'],
        queryFn: () => get(businessApiRoute('/forms/booking')),
    });
}

export function useBillingStatusQuery() {
    return useQuery({
        queryKey: [getBusinessId(), 'billing-status'],
        queryFn: () => get(businessApiRoute('/billing/status')),
    });
}

export function useClientsQuery() {
    return useQuery<Array<ClientModel>>({
        queryKey: [getBusinessId(), 'clients'],
        queryFn: () => get(businessApiRoute('/clients')),
    });
}

export function useReportsQuery() {
    return useQuery({
        queryKey: [getBusinessId(), 'reports'],
        queryFn: () => get(businessApiRoute('/reports')),
    });
}

export function useSpecificReportQuery(reportId) {
    return useQuery({
        queryKey: [getBusinessId(), 'reports', parseInt(reportId)],
        queryFn: () => get(businessApiRoute(`/reports/${reportId}`)),
    });
}

export function setSpecificReportQuery(reportId, data) {
    queryClient.setQueryData(
        [getBusinessId(), 'reports', parseInt(reportId)],
        old => data,
    );
}

export function setClientsQuery(formData) {
    queryClient.setQueryData([getBusinessId(), 'clients'], old => formData);
}

export function useClientDetailsQuery(clientId) {
    return useQuery({
        queryKey: ['client', parseInt(clientId)],
        queryFn: async () => {
            if (!clientId) {
                return {};
            }
            return await get(businessApiRoute(`/clients/${clientId}`));
        },
    });
}

export function setClientDetailsQuery(clientId, formData) {
    queryClient.setQueryData(['client', parseInt(clientId)], old => formData);
}

export function useFormDetailsQuery(formId) {
    return useQuery({
        queryKey: ['form', parseInt(formId)],
        queryFn: async () => {
            const data = await get(businessApiRoute(`/forms/${formId}`));

            return {
                ...data,
                formSections: (data?.sections || []).filter(
                    s => !s.section.settings?.thankyou,
                ),
            };
        },
        staleTime: 500,
    });
}

export function setFormDetailsQuery(formId, formData) {
    queryClient.setQueryData(['form', parseInt(formId)], old => ({
        ...formData,
        formSections: (formData.sections || [])
            .filter(s => !s.section.settings?.thankyou)
            .sort((s1, s2) => s1.section.order - s2.section.order),
    }));
}

export function useFormSharingQuery(formId) {
    return useQuery({
        queryKey: ['form-sharing', parseInt(formId)],
        queryFn: () => get(businessApiRoute(`/forms/${formId}/sharing`)),
    });
}

export function useFlowQuery(flowableType, flowableId) {
    return useQuery({
        queryKey: ['flow', flowableType, parseInt(flowableId)],
        queryFn: () =>
            get(businessApiRoute(`/flows/${flowableType}/${flowableId}`)),
    });
}

export function setFlowQuery(flowableType, flowableId, data) {
    queryClient.setQueryData(
        ['flow', flowableType, parseInt(flowableId)],
        old => data,
    );
}

export function useCalendarEventsQuery(start, end) {
    return useQuery<Event>({
        queryKey: [getBusinessId(), 'calendar-events', start, end],
        queryFn: () => {
            return get(
                businessApiRoute(
                    `/calendar/events?start=${start.toISOString()}&end=${end.toISOString()}`,
                ),
            );
        },
    });
}

export function useCalendarViewDetailsQuery(start, end) {
    return useQuery({
        queryKey: [getBusinessId(), 'calendar-view-details', start, end],
        queryFn: async () => {
            return await get(
                businessApiRoute(
                    `/calendar/view-details?start=${start.toISOString()}&end=${end.toISOString()}`,
                ),
            );
        },
    });
}

export function useCalendarColorDetailsQuery() {
    return useQuery({
        queryKey: [getBusinessId(), 'calendar-color-details'],
        queryFn: async () => {
            return await get(businessApiRoute(`/calendar/color-details`));
        },
    });
}

export function setCalendarColorDetailsQuery(data) {
    queryClient.setQueryData(
        [getBusinessId(), 'calendar-color-details'],
        old => data,
    );
}

export function useEventTypesQuery(includeCategories?: boolean) {
    return useQuery({
        queryKey: [getBusinessId(), 'event-types', !!includeCategories],
        queryFn: () =>
            get(
                businessApiRoute(
                    `/event-types${includeCategories ? '?categories=true' : ''}`,
                ),
            ),
    });
}

export function setEventTypesQuery(data, includeCategories) {
    queryClient.setQueryData(
        [getBusinessId(), 'event-types', !!includeCategories],
        old => data,
    );
}

export function useEventTypeDetailsQuery(typeId, params) {
    typeId = parseInt(typeId);
    return useQuery({
        queryKey: [
            'event-type',
            getBusinessId() /*for default id=0*/,
            typeId,
            params,
        ],
        queryFn: async () => {
            return await get(
                businessApiRoute(
                    `/event-types/${typeId}${params ? `?${params}` : ''}`,
                ),
            );
        },
    });
}

export function useBookableResourcesQuery(isLimited) {
    isLimited = !!isLimited;

    return useQuery({
        queryKey: [getBusinessId(), 'bookable-resources', isLimited],
        queryFn: () =>
            get(
                businessApiRoute(
                    `/bookable-resources${isLimited ? '?limited=true' : ''}`,
                ),
            ),
    });
}

export function setBookableResourcesQuery(data, isLimited) {
    isLimited = !!isLimited;

    queryClient.setQueryData(
        [getBusinessId(), 'bookable-resources', isLimited],
        old => data,
    );
}

export function useAvailabilityGroupsQuery(resourceId) {
    resourceId = parseInt(resourceId);

    return useQuery({
        queryKey: ['availability-groups', resourceId],
        queryFn: () =>
            get(
                businessApiRoute(
                    `/scheduling/availability/${resourceId}/availability-groups`,
                ),
            ),
    });
}

export function setAvailabilityGroupsQuery(resourceId, data) {
    queryClient.setQueryData(
        ['availability-groups', parseInt(resourceId)],
        old => data,
    );
}

export function useSpecificHoursQuery(resourceId, availabilityGroup) {
    resourceId = parseInt(resourceId);
    availabilityGroup = parseInt(availabilityGroup);

    return useQuery({
        queryKey: ['specific-hours', resourceId, availabilityGroup],
        queryFn: () =>
            get(
                businessApiRoute(
                    `/scheduling/availability/${resourceId}/specific-hours?availabilityGroup=${availabilityGroup}`,
                ),
            ),
    });
}

export function setSpecificHoursQuery(resourceId, availabilityGroup, data) {
    queryClient.setQueryData(
        ['specific-hours', parseInt(resourceId), parseInt(availabilityGroup)],
        old => data,
    );
}

export function useLimitsQuery() {
    return useQuery({
        queryKey: [getBusinessId(), 'limits'],
        queryFn: () => get(businessApiRoute(`/scheduling/limits`)),
    });
}

export function setLimitsQuery(data) {
    queryClient.setQueryData([getBusinessId(), 'limits'], old => data);
}

export function useEventDetailsQuery(id) {
    id = parseInt(id);
    return useQuery({
        queryKey: ['event-details', id],
        queryFn: async () => {
            if (!id) {
                return {};
            }
            return await get(businessApiRoute(`/calendar/events/${id}`));
        },
        retry: smartRetry,
    });
}

export function setEventDetailsQuery(id, data) {
    queryClient.setQueryData(['event-details', parseInt(id)], old => data);
}

export function useClassDetailsQuery(id) {
    id = parseInt(id);
    return useQuery({
        queryKey: ['class-details', id],
        queryFn: async () => {
            if (!id) {
                return {};
            }
            return await get(businessApiRoute(`/calendar/class/${id}`));
        },
    });
}

export function setClassDetailsQuery(id, data) {
    queryClient.setQueryData(['class-details', parseInt(id)], old => data);
}

export function useOtherIntegrationsQuery() {
    return useQuery({
        queryKey: [getBusinessId(), 'other-integrations'],
        queryFn: () => get(businessApiRoute(`/integrations/other`)),
    });
}

export function setOtherIntegrationsQuery(data) {
    queryClient.setQueryData(
        [getBusinessId(), 'other-integrations'],
        old => data,
    );
}

export function useZoomQuery() {
    return useQuery({
        queryKey: [getBusinessId(), 'zoom-integration'],
        queryFn: () => get(businessApiRoute(`/integrations/other/zoom`)),
    });
}

export function setZoomQuery(data) {
    queryClient.setQueryData(
        [getBusinessId(), 'zoom-integration'],
        old => data,
    );
}

export function useCalendarIntegrationsQuery() {
    return useQuery({
        queryKey: [getBusinessId(), 'calendar-integrations'],
        queryFn: () => get(businessApiRoute(`/integrations/calendars`)),
    });
}

export function setCalendarIntegrationsQuery(data) {
    queryClient.setQueryData(
        [getBusinessId(), 'calendar-integrations'],
        old => data,
    );
}

export function useSyncingBookableResourcesQuery() {
    return useQuery({
        queryKey: [getBusinessId(), 'syncing-bookable-resources'],
        queryFn: () => get(businessApiRoute(`/integrations/calendars`)),
    });
}

export function useFormSubmissionSignatureQuery(formSubmissionId: number) {
    return useQuery<{
        signatureStatus: 'SignatureValid' | 'SignatureInvalid' | 'Unsigned';
        signedDocUrl: string;
    }>({
        queryKey: ['signature', formSubmissionId],
        queryFn: async () => {
            if (!formSubmissionId) {
                return {};
            }
            return await get(
                businessApiRoute(`/signature/${formSubmissionId}`),
            );
        },
    });
}

export function useFormSubmissionDetailsQuery(
    submissionId?: number,
    autoFetch = true,
) {
    return useQuery<FormSubmissionDetails>({
        queryKey: ['submission-details', parseInt(submissionId)],
        queryFn: async () => {
            if (!parseInt(submissionId)) {
                return {};
            }
            return await get(businessApiRoute(`/submissions/${submissionId}`));
        },
        enabled: autoFetch || submissionId == null,
    });
}

export function setFormSubmissionDetailsQuery(submissionId, data) {
    queryClient.setQueryData(
        ['submission-details', parseInt(submissionId)],
        old => data,
    );
}

export function useUsersQuery() {
    return useQuery({
        queryKey: [getBusinessId(), 'users'],
        queryFn: () => get(businessApiRoute(`/users`)),
    });
}

export function setUsersQuery(data) {
    queryClient.setQueryData([getBusinessId(), 'users'], old => data);
}

export function useAuditLogsQuery(type, id) {
    return useQuery({
        queryKey: [getBusinessId(), 'audit-logs', type, parseInt(id)],
        queryFn: async () => {
            if (!id) {
                return [];
            }
            return await get(businessApiRoute(`/audit-logs/${type}/${id}`));
        },
    });
}

export function usePaymentHistory(type, id) {
    return useQuery({
        queryKey: [getBusinessId(), 'payment-history', type, parseInt(id)],
        queryFn: async () => {
            if (!id) {
                return [];
            }
            return await get(
                businessApiRoute(`/payments/history/${type}/${id}`),
            );
        },
    });
}

export function setPaymentHistory(type, id, data) {
    queryClient.setQueryData(
        [getBusinessId(), 'payment-history', type, parseInt(id)],
        old => data,
    );
}

export function useUnplashQuery(search) {
    return useQuery({
        queryKey: ['unsplash', search],
        queryFn: async () => {
            if (!search) {
                return [];
            }
            return await get(
                userApiRoute(`/unsplash?q=${encodeURIComponent(search)}`),
            );
        },
    });
}

export let ONBOARDING_CACHE = [];
export function useOnboardingQuery(isMobile) {
    return useQuery({
        queryKey: [getBusinessId(), 'onboarding', isMobile ? 1 : 0],
        queryFn: async () => {
            const result = await get(
                businessApiRoute(
                    `/onboarding?mobile=${isMobile ? 'true' : 'false'}`,
                ),
            );
            ONBOARDING_CACHE = result;
            return result;
        },
    });
}

export function setOnboardingQuery(data, isMobile) {
    queryClient.setQueryData(
        [getBusinessId(), 'onboarding', isMobile ? 1 : 0],
        old => {
            ONBOARDING_CACHE = data;
            return data;
        },
    );
}

export function useClientSearchQuery(search) {
    return useQuery({
        queryKey: [getBusinessId(), 'client-search', search],
        queryFn: async () => {
            if (!search) {
                return [];
            }
            return await get(
                businessApiRoute(
                    `/clients/search?q=${encodeURIComponent(search)}`,
                ),
            );
        },
    });
}

export function usePackagesQuery(type: string) {
    return useQuery({
        queryKey: [getBusinessId(), 'packages', type],
        queryFn: async () => {
            return await get(businessApiRoute(`/packages?type=${type}`));
        },
    });
}

export function setPackagesQuery(data, type: string) {
    queryClient.setQueryData([getBusinessId(), 'packages', type], old => data);
}

export function usePackageDetailsQuery(packageId) {
    const result = usePackagesQuery('');

    return {
        ...result,
        data: (result?.data?.packages || []).find(
            p => parseInt(p.id) === parseInt(packageId),
        ),
    };
}

export function usePackageGrantsQuery(opts) {
    const params = new URLSearchParams(opts).toString();

    return useQuery({
        queryKey: [getBusinessId(), 'packages-grants', params],
        queryFn: async () => {
            return await get(businessApiRoute(`/packages/grants?${params}`));
        },
    });
}

export function useAssignablePackageGrantsQuery(type, id) {
    id = parseInt(id);

    return useQuery({
        queryKey: [getBusinessId(), 'assignable-packages-grants', type, id],
        queryFn: async () => {
            if (!id || isNaN(id)) {
                return {};
            }
            return await get(
                businessApiRoute(
                    `/packages/grants/assignable-to/${type}/${id}`,
                ),
            );
        },
    });
}

export function setAssignablePackageGrantsQuery(type, id, data) {
    id = parseInt(id);
    queryClient.setQueryData(
        [getBusinessId(), 'assignable-packages-grants', type, id],
        old => data,
    );
}

export function useCustomFieldsSchemaQuery(type) {
    return useQuery({
        queryKey: [getBusinessId(), 'custom-fields-schema', type],
        queryFn: async () => {
            return await get(businessApiRoute(`/custom-fields/schema/${type}`));
        },
        staleTime: 1000 * 15, // This doesn't change often
    });
}

export function setCustomFieldsSchemaQuery(type, data) {
    queryClient.setQueryData(
        [getBusinessId(), 'custom-fields-schema', type],
        old => data,
    );
}

export function useSquareDetailsQuery() {
    return useQuery({
        queryKey: [getBusinessId(), 'square-details'],
        queryFn: async () => {
            return await get(
                businessApiRoute(`/integrations/payments/square/details`),
            );
        },
    });
}

export function setSquareDetailsQuery(data) {
    queryClient.setQueryData([getBusinessId(), 'square-details'], old => data);
}

export function useCouponsQuery() {
    return useQuery({
        queryKey: [getBusinessId(), 'coupons'],
        queryFn: async () => {
            return await get(businessApiRoute(`/coupons`));
        },
    });
}

export function useCouponsDetailsQuery(id) {
    const couponId = parseInt(id);

    return useQuery({
        queryKey: [getBusinessId(), 'coupon-details', couponId],
        queryFn: async () => {
            if (couponId === 0) {
                return {};
            }
            return await get(businessApiRoute(`/coupons/${couponId}`));
        },
    });
}

export function useCouponUsageQuery(id) {
    const couponId = parseInt(id);

    return useQuery({
        queryKey: [getBusinessId(), 'coupon-usage', couponId],
        queryFn: async () => {
            return await get(businessApiRoute(`/coupons/${couponId}/usage`));
        },
    });
}

export function useWaitlistSettingsQuery() {
    return useQuery({
        queryKey: [getBusinessId(), 'waitlist-settings'],
        queryFn: async () => {
            return await get(businessApiRoute(`/waitlist/settings`));
        },
    });
}

export function useWaitlistListQuery() {
    return useQuery({
        queryKey: [getBusinessId(), 'waitlist-list'],
        queryFn: async () => {
            return await get(businessApiRoute(`/waitlist`));
        },
    });
}

export function setWaitlistListQuery(data) {
    queryClient.setQueryData([getBusinessId(), 'waitlist-list'], old => data);
}

export function useClassWaitlistListQuery(instanceId) {
    instanceId = parseInt(instanceId);

    return useQuery({
        queryKey: [getBusinessId(), 'waitlist-list-class', instanceId],
        queryFn: async () => {
            if (!instanceId) {
                return {};
            }

            return await get(businessApiRoute(`/waitlist/class/${instanceId}`));
        },
    });
}

export function useGiftCertificatesQuery() {
    return useQuery({
        queryKey: [getBusinessId(), 'gift-certificates'],
        queryFn: async () => {
            return await get(businessApiRoute(`/gift-certificates`));
        },
    });
}

export function useGiftCertificateDetailsQuery(id) {
    id = parseInt(id);

    return useQuery({
        queryKey: [getBusinessId(), 'gift-certificate-details', id],
        queryFn: async () => {
            if (!id) {
                return {};
            }

            return await get(businessApiRoute(`/gift-certificates/${id}`));
        },
    });
}

export function useSchedulingAddonsQuery(eventTypeId, formId) {
    return useQuery({
        queryKey: [getBusinessId(), 'scheduling-addons', eventTypeId, formId],
        queryFn: async () => {
            return await get(
                businessApiRoute(`/schedule/addons/${eventTypeId}/${formId}`),
            );
        },
    });
}

export function useSchedulingFormQuery(eventTypeId, formId) {
    return useQuery({
        queryKey: [getBusinessId(), 'scheduling-forms', eventTypeId, formId],
        queryFn: async () => {
            return await get(
                businessApiRoute(`/schedule/form/${eventTypeId}/${formId}`),
            );
        },
    });
}

export function useBlockDetailsQuery(blockId) {
    return useQuery({
        queryKey: [getBusinessId(), 'block-details', parseInt(blockId)],
        queryFn: async () => {
            if (!blockId) {
                return null;
            }
            return await get(businessApiRoute(`/calendar/blocks/${blockId}`));
        },
        enabled: !!blockId,
    });
}

export function useAssistantRecentConvosQuery() {
    return useQuery({
        queryKey: [getBusinessId(), 'assistant-convos'],
        queryFn: async () => {
            return await get(businessApiRoute(`/assistant/recent`));
        },
    });
}
